/**
 * useActiveDubRun.ts — PLAT-11714 (extracted for PLAT-11570 / PLAT-11688)
 *
 * "Is a dub running here?", the one way. `DubbingStore` holds a single run for
 * the whole workspace, so every surface that reports one has to answer the same
 * two questions first — is it THIS story's, and (where the surface is about one
 * language) is it THIS language's. Extracted from `DubRunChip` when the
 * Translation Review needed the same answer for its player header.
 */
import { useDubbingStore, useStore } from '@src/stores-v2/StoreContext';
import { normalizeLangKey } from '@src/utility/bcp47';

import type { DubRun } from '@src/stores-v2/DubbingStore';

/**
 * The dub run in flight for the current story, or `null`. Pass `language` to
 * narrow it to a run for that language as well — a surface that speaks for one
 * language must not report another's run as its own.
 *
 * Reads observable store state, so the caller must be an `observer`.
 *
 * `rootStore.storyId` is bridged from `videoCreator.storyId` by
 * `useTranslationStoreHydration`, which every surface using this calls; it is
 * null until that effect runs, so an unbridged surface reports no run rather
 * than the wrong one.
 */
export function useActiveDubRun(language?: string): DubRun | null {
  const rootStore = useStore();
  const dubbingStore = useDubbingStore();
  // Transcript-only test harnesses build a root store without a dubbingStore;
  // in the app it is always there.
  return narrowRun(
    dubbingStore?.activeRun ?? null,
    rootStore.storyId,
    language,
  );
}

/**
 * The last dub run that SUCCEEDED for the current story (and `language`, when
 * passed), or `null`. Narrowed like {@link useActiveDubRun}; a new object each
 * time a run completes, so a change of identity is a new dub.
 */
export function useLastCompletedDubRun(language?: string): DubRun | null {
  const rootStore = useStore();
  const dubbingStore = useDubbingStore();
  return narrowRun(
    dubbingStore?.lastCompletedRun ?? null,
    rootStore.storyId,
    language,
  );
}

function narrowRun(
  run: DubRun | null,
  storyId: string | null | undefined,
  language: string | undefined,
): DubRun | null {
  if (run === null || run.storyId !== storyId) return null;
  if (
    language !== undefined &&
    normalizeLangKey(run.targetLanguage) !== normalizeLangKey(language)
  ) {
    return null;
  }
  return run;
}
